import { LocalStorageRegistry } from '@/constants/localStorageRegistry'
import { useLocalStorage } from '@/hooks/useLocalStorage'

type LocalUserPreferences = {
  /**
   * Indicates if name should be pre-populated in forms
   */
  shouldPrePopulateName: boolean
  /**
   * Setter function to update shouldPrePopulateName
   */
  setShouldPrePopulateName: (shouldPrePopulateName: boolean) => void
  /**
   * The name saved by the user
   */
  savedName: string
  /**
   * Setter function to update savedName
   */
  setSavedName: (savedName: string) => void
  /**
   * Function to update savedName taking user setting shouldPrePopulateName into account
   */
  handleUpdateSavedName: (name: string) => void
}

type LocalUserPreferencesValues = {
  shouldPrePopulateName: boolean
  savedName: string
}

/**
 * Hook to manage user preferences stored in local storage
 * @returns An object containing:
 * - shouldPrePopulateName: boolean indicating if name should be pre-populated
 * - setShouldPrePopulateName: function to update the shouldPrePopulateName preference
 */
export const useLocalUserPreferences = (): LocalUserPreferences => {
  const [localUserPreferences, setLocalUserPreferences] =
    useLocalStorage<LocalUserPreferencesValues>(LocalStorageRegistry.LOCAL_USER_PREFERENCES, {
      shouldPrePopulateName: false,
      savedName: '',
    })

  const setShouldPrePopulateName = (shouldPrePopulateName: boolean) => {
    setLocalUserPreferences({
      ...localUserPreferences,
      shouldPrePopulateName,
    })
  }

  const setSavedName = (savedName: string) => {
    setLocalUserPreferences({
      ...localUserPreferences,
      savedName,
    })
  }

  const handleUpdateSavedName = (name: string) => {
    if (shouldPrePopulateName) {
      setSavedName(name)
    } else {
      setSavedName('')
    }
  }

  const { shouldPrePopulateName, savedName } = localUserPreferences

  return {
    shouldPrePopulateName,
    setShouldPrePopulateName,
    savedName,
    setSavedName,
    handleUpdateSavedName,
  }
}
